import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatIDR, API_BASE } from "@/lib/api";
import { PageHeader, Table, Badge, Modal, Field, Input, Select, Btn, useDirtyGuard, ConfirmClose } from "@/components/erp/ui";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CustomerMergeModal } from "@/components/erp/CustomerMergeModal";
import { CustomerVisits } from "@/components/erp/sales/CustomerVisits";
import { AdSelect } from "@/components/erp/social/AdsReport";
import { SpamPurge } from "@/components/erp/SpamPurge";
import { Plus, Trash2, Pencil, FileText, ClipboardList, Receipt, CalendarCheck, ImagePlus, X, Loader2, Search, ShieldCheck, Package, Users } from "lucide-react";

const EMPTY = { name: "", company: "", whatsapp: "", email: "", phone: "", address: "", npwp: "", npwp_photo: "", type: "Lead", source: "Manual", notes: "", ad_id: "" };
const SOURCES = ["Calculator", "Sales", "Lead", "Marketing", "Manual"];
const SORTS = [
  ["name_az", "Nama A-Z"], ["name_za", "Nama Z-A"],
  ["newest", "Terbaru"], ["oldest", "Terlama"], ["modified", "Terakhir diubah"],
];

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [seg, setSeg] = useState("leads");
  const { user } = useAuth();
  const [dupOpen, setDupOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [adMap, setAdMap] = useState({});
  useEffect(() => { api.get("/social/ads").then(({ data }) => setAdMap(Object.fromEntries(data.map((a) => [a.id, `${a.title} · ${a.platform}`])))).catch(() => {}); }, []);
  const [editId, setEditId] = useState(null);
  const [sort, setSort] = useState("newest");
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);
  const [uploadingBid, setUploadingBid] = useState(null);
  const g = useDirtyGuard();
  const forceClose = () => { g.clear(); setOpen(false); setForm(EMPTY); setEditId(null); setNpwpFile(null); };
  const tryClose = () => g.attemptClose({ ...form, npwp_file: npwpFile ? npwpFile.name : "" }, forceClose);
  const [npwpOnly, setNpwpOnly] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/customers").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const cid = params.get("customer");
    if (cid) { api.get(`/customers/${cid}`).then(({ data }) => setDetail(data)).catch(() => {}); setParams({}, { replace: true }); }
  // eslint-disable-next-line
  }, []);

  const refreshDetail = async (cid) => {
    try { const { data } = await api.get(`/customers/${cid}`); setDetail(data); } catch { /* keep */ }
  };
  const uploadVisitPhoto = async (bid, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploadingBid(bid);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        await api.post(`/bookings/${bid}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Foto hasil kunjungan tersimpan");
      await refreshDetail(detail.id);
    } catch { toast.error("Gagal mengunggah foto"); }
    setUploadingBid(null);
  };
  const deleteVisitPhoto = async (bid, pid) => {
    try { await api.delete(`/bookings/${bid}/photo/${pid}`); await refreshDetail(detail.id); } catch { toast.error("Gagal menghapus foto"); }
  };

  const [npwpBusy, setNpwpBusy] = useState(false);
  const [npwpFile, setNpwpFile] = useState(null);

  const custNpwpUpload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    if (!editId) { setNpwpFile(f); toast.success("File NPWP dipilih — akan diunggah setelah customer disimpan"); return; }
    setNpwpBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/customers/${editId}/npwp-photo`, fd);
      setForm((s) => ({ ...s, npwp: data.npwp || s.npwp, company: data.company || s.company, address: data.address || s.address, npwp_photo: data.photo }));
      toast.success(data.npwp ? "NPWP terdeteksi & terisi otomatis — tersambung ke quotation" : "File tersimpan, lengkapi manual");
    } catch (err) { const d = err.response?.data?.detail; toast.error(typeof d === "string" ? d : "Gagal membaca NPWP"); }
    setNpwpBusy(false);
  };
  const custNpwpDelete = async () => {
    if (!editId) { setNpwpFile(null); setForm((s) => ({ ...s, npwp: "", npwp_photo: "" })); return; }
    try {
      await api.delete(`/customers/${editId}/npwp`);
      setForm((s) => ({ ...s, npwp: "", npwp_photo: "" }));
      toast.success("NPWP dihapus — silakan lampirkan NPWP baru (tersambung ke quotation)");
      load();
    } catch { toast.error("Gagal menghapus NPWP"); }
  };

  const sorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    const isMem = (c) => ["Member Online", "Katalog Online"].includes(c.source);
    let base = rows.filter((c) => (seg === "member" ? isMem(c) : !isMem(c)));
    base = npwpOnly ? base.filter((c) => c.npwp && c.address && c.company) : base;
    const r = q ? base.filter((c) => [c.code, c.name, c.company, c.whatsapp, c.phone, c.email, c.type, c.source].some((v) => (v || "").toLowerCase().includes(q))) : [...base];
    const t = (c) => c.updated_at || c.created_at || "";
    if (sort === "name_az") r.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (sort === "name_za") r.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    else if (sort === "newest") r.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else if (sort === "oldest") r.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else if (sort === "modified") r.sort((a, b) => t(b).localeCompare(t(a)));
    return r;
  }, [rows, sort, query, npwpOnly, seg]);
  const memCount = rows.filter((c) => ["Member Online", "Katalog Online"].includes(c.source)).length;

  const save = async () => {
    if (!form.name) { toast.error("Nama wajib diisi"); return; }
    try {
      if (editId) await api.put(`/customers/${editId}`, form);
      else {
        const { data: created } = await api.post("/customers", form);
        if (npwpFile) {
          try {
            const fd = new FormData(); fd.append("file", npwpFile);
            const { data } = await api.post(`/customers/${created.id}/npwp-photo`, fd);
            toast.success(data.npwp ? `NPWP terdeteksi: ${data.npwp} — tersambung ke quotation` : "Customer tersimpan; lampiran NPWP terunggah (lengkapi nomor manual bila perlu)");
          } catch { toast.error("Customer tersimpan, tapi lampiran NPWP gagal diunggah"); }
        }
      }
      toast.success("Tersimpan"); setOpen(false); setForm(EMPTY); setEditId(null); setNpwpFile(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };
  const del = async (id) => { if (!window.confirm("Hapus customer?")) return; await api.delete(`/customers/${id}`); load(); };
  const edit = (c) => { const f = { ...EMPTY, ...c }; setForm(f); setNpwpFile(null); g.snapshot({ ...f, npwp_file: "" }); setEditId(c.id); setOpen(true); };
  const openDetail = async (c) => {
    try { const { data } = await api.get(`/customers/${c.id}`); setDetail(data); } catch { toast.error("Gagal memuat detail"); }
  };

  const Section = ({ icon: Icon, title, items, render }) => (
    <div>
      <div className="flex items-center gap-2 text-xs font-mono text-slate-500 mb-2"><Icon className="w-3.5 h-3.5" /> {title} ({items?.length || 0})</div>
      {(!items || items.length === 0) ? <p className="text-sm text-slate-400">Belum ada.</p> :
        <div className="space-y-1.5">{items.map(render)}</div>}
    </div>
  );

  return (
    <div>
      <PageHeader title="Customers" subtitle="CRM — satu customer, banyak project." action={
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="customer-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari nama, kode, kontak…" className="border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm w-56" />
          </div>
          <Select data-testid="customer-sort" value={sort} onChange={(e) => setSort(e.target.value)}>{SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select>
          <button data-testid="customer-npwp-filter" onClick={() => setNpwpOnly((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${npwpOnly ? "bg-emerald-500 text-white border-emerald-500" : "border-slate-300 text-slate-600 hover:border-slate-400"}`}>
            <ShieldCheck className="w-4 h-4" /> NPWP lengkap
          </button>
          <Btn variant="ghost" data-testid="customer-dup-btn" onClick={() => setDupOpen(true)}><Users className="w-4 h-4" /> Cek Duplikat</Btn>
          <Btn data-testid="add-customer-btn" onClick={() => { setForm(EMPTY); setEditId(null); setNpwpFile(null); g.snapshot({ ...EMPTY, npwp_file: "" }); setOpen(true); }}><Plus className="w-4 h-4" /> Customer</Btn>
        </div>} />
      <CustomerMergeModal open={dupOpen} onClose={() => setDupOpen(false)} isOwner={user?.role === "owner"} onMerged={load} />
      <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 mb-3" data-testid="customer-seg">
        {[["leads", `Leads & Sales (${rows.length - memCount})`], ["member", `Member Online (${memCount})`]].map(([k, l]) => <button key={k} type="button" data-testid={`customer-seg-${k}`} onClick={() => setSeg(k)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${seg === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>)}
      </div>
      {seg === "member" && <p className="text-xs text-slate-500 mb-3" data-testid="customer-seg-note">Customer yang log in & belanja / simulasi langsung di website. Terpisah dari customer hasil leads / sales. Ikon perisai = hapus member spam (admin utama).</p>}
      <Table search={false} testid="customers-table" columns={["Kode", "Nama", "Perusahaan", "Kontak", "Tipe", "Sumber", ""]} rows={sorted} renderRow={(c) => (
        <tr key={c.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
          <td className="px-4 py-3 font-mono text-xs">{c.code}</td>
          <td className="px-4 py-3 font-medium"><button data-testid={`customer-name-${c.id}`} onClick={() => openDetail(c)} className="hover:text-sky-600 hover:underline">{c.name}</button>{c.active_consignments > 0 && <span data-testid={`cust-consign-badge-${c.id}`} title="Unit titipan di FOMU" className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">{c.active_consignments} titip</span>}</td>
          <td className="px-4 py-3">{c.company || "-"}{(c.npwp && c.address && c.company) && <span data-testid={`npwp-complete-${c.id}`} className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700">NPWP lengkap</span>}</td>
          <td className="px-4 py-3 text-slate-500">{c.whatsapp || c.email || "-"}</td>
          <td className="px-4 py-3"><Badge>{c.type}</Badge></td>
          <td className="px-4 py-3 text-slate-500">{c.source}{c.ad_id && adMap[c.ad_id] && <div className="text-[11px] text-amber-700" data-testid={`cust-ad-${c.id}`}>iklan: {adMap[c.ad_id]}</div>}</td>
          <td className="px-4 py-3 text-right whitespace-nowrap">
            {c.member_id && user?.role === "owner" && <SpamPurge c={c} onDone={load} />}
            <button data-testid={`edit-customer-${c.id}`} onClick={() => edit(c)} className="text-slate-400 hover:text-slate-900 mr-3"><Pencil className="w-4 h-4" /></button>
            <button data-testid={`delete-customer-${c.id}`} onClick={() => del(c.id)} className="text-slate-400 hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>
          </td>
        </tr>
      )} />

      <Modal open={open} onClose={tryClose} title={editId ? "Edit Customer" : "Customer Baru"}
        footer={<><Btn variant="ghost" onClick={tryClose}>Batal</Btn><Btn data-testid="save-customer-btn" onClick={save}>Simpan</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Nama *"><Input data-testid="cf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Perusahaan"><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></Field>
          <Field label="WhatsApp"><Input data-testid="cf-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} /></Field>
          <Field label="Email"><Input data-testid="cf-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Telepon"><Input data-testid="cf-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="NPWP">
            <div className="flex items-center gap-1.5">
              <Input data-testid="cf-npwp" value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} placeholder="15 digit" />
            </div>
          </Field>
          <Field label="Tipe"><Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>{["Lead", "Customer", "VIP", "Corporate"].map(t => <option key={t}>{t}</option>)}</Select></Field>
          <Field label="Sumber"><Select data-testid="cf-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>{SOURCES.map(s => <option key={s}>{s}</option>)}</Select></Field>
          <Field label="Dari iklan (opsional)"><AdSelect testid="cf-ad" className="w-full" value={form.ad_id} onChange={(v) => setForm({ ...form, ad_id: v })} /></Field>
          <div className="col-span-2"><Field label="Alamat"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field></div>
          <div className="col-span-2 border-t border-slate-100 pt-3" data-testid="cf-npwp-section">
            <div className="text-xs font-mono text-slate-500 mb-2">LAMPIRAN NPWP</div>
            {!editId ? (
              <>
                <p className="text-[11px] text-slate-400 mb-2">Lampirkan kartu NPWP (<b>.pdf / .jpg / .png</b>) sekarang juga bisa — file akan <b>diunggah otomatis setelah customer disimpan</b>, lalu nomor, perusahaan & alamat terdeteksi dan <b>tersambung ke quotation</b>.</p>
                <label data-testid="cf-npwp-upload" className="block border-2 border-dashed border-slate-300 rounded-xl p-4 text-center cursor-pointer hover:border-sky-400 transition-colors">
                  <ImagePlus className="w-6 h-6 mx-auto text-slate-400" />
                  <div className="text-sm text-slate-600 mt-1">Klik untuk pilih file NPWP (PDF/JPG/PNG)</div>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={custNpwpUpload} />
                </label>
                {npwpFile && (
                  <div className="mt-2">
                    {npwpFile.type === "application/pdf" ? (
                      <div data-testid="cf-npwp-pending" className="flex items-center gap-2 text-sm text-sky-700 border border-sky-200 bg-sky-50 rounded-lg px-3 py-2"><FileText className="w-4 h-4" /> {npwpFile.name} <span className="text-[10px] text-sky-500">(terunggah setelah Simpan)</span></div>
                    ) : (
                      <img src={URL.createObjectURL(npwpFile)} alt="NPWP" data-testid="cf-npwp-preview"
                        className="w-full max-h-40 object-contain rounded-lg border border-slate-200 bg-slate-50" />
                    )}
                    <button type="button" data-testid="cf-npwp-delete" onClick={custNpwpDelete}
                      className="mt-2 inline-flex items-center gap-1.5 text-sm text-rose-500 hover:text-rose-600"><Trash2 className="w-4 h-4" /> Hapus lampiran & pilih ulang</button>
                  </div>
                )}
              </>
            ) : (<>
              <p className="text-[11px] text-slate-400 mb-2">Unggah kartu NPWP (<b>.pdf / .jpg / .png</b>) — nomor, nama perusahaan & alamat terdeteksi otomatis dan tersimpan ke data customer serta <b>tersambung ke quotation</b>. Bisa diedit manual bila deteksi kurang tepat, atau dihapus lalu dilampirkan ulang bila ada NPWP baru.</p>
              <label data-testid="cf-npwp-upload" className="block border-2 border-dashed border-slate-300 rounded-xl p-4 text-center cursor-pointer hover:border-sky-400 transition-colors">
                {npwpBusy ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-sky-500" /> : <ImagePlus className="w-6 h-6 mx-auto text-slate-400" />}
                <div className="text-sm text-slate-600 mt-1">{npwpBusy ? "Membaca file NPWP…" : "Klik untuk unggah NPWP (PDF/JPG/PNG)"}</div>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" disabled={npwpBusy} onChange={custNpwpUpload} />
              </label>
              {form.npwp_photo && (String(form.npwp_photo).endsWith(".pdf") ? (
                <a href={`${API_BASE}/files/${form.npwp_photo}`} target="_blank" rel="noreferrer" data-testid="cf-npwp-pdf-link"
                  className="mt-2 flex items-center gap-2 text-sm text-sky-600 border border-slate-200 rounded-lg px-3 py-2 hover:bg-sky-50"><FileText className="w-4 h-4" /> Lihat file PDF NPWP</a>
              ) : (
                <img src={`${API_BASE}/files/${form.npwp_photo}`} alt="NPWP" data-testid="cf-npwp-preview"
                  className="mt-2 w-full max-h-40 object-contain rounded-lg border border-slate-200 bg-slate-50" />
              ))}
              {(form.npwp || form.npwp_photo) && (
                <button type="button" data-testid="cf-npwp-delete" onClick={custNpwpDelete}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm text-rose-500 hover:text-rose-600"><Trash2 className="w-4 h-4" /> Hapus NPWP & lampirkan ulang</button>
              )}
            </>)}
          </div>
        </div>
      </Modal>
      <ConfirmClose open={g.confirmOpen} testid="customer-confirm-close" onCancel={() => g.setConfirmOpen(false)} onDiscard={forceClose} onSave={async () => { g.setConfirmOpen(false); await save(); }} />

      <Modal open={!!detail} onClose={() => setDetail(null)} title={`Customer · ${detail?.name}`}>
        {detail && (
          <div className="space-y-5" data-testid="customer-detail">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-xs text-slate-500">Perusahaan</div>{detail.company || "-"}</div>
              <div><div className="text-xs text-slate-500">Sumber</div><Badge>{detail.source}</Badge>{detail.ad_id && <div className="text-xs text-amber-700 mt-1" data-testid="cust-detail-ad">Dari iklan: {adMap[detail.ad_id] || "(iklan dihapus)"}</div>}</div>
              <div><div className="text-xs text-slate-500">WhatsApp</div>{detail.whatsapp || "-"}</div>
              <div><div className="text-xs text-slate-500">Email</div>{detail.email || "-"}</div>
              <div><div className="text-xs text-slate-500">NPWP</div><span data-testid="cust-npwp">{detail.npwp || "-"}</span></div>
              <div><div className="text-xs text-slate-500">Alamat</div><span data-testid="cust-address">{detail.address || "-"}</span></div>
            </div>
            <CustomerVisits cid={detail.id} />
            {detail.npwp_photo && (
              <div data-testid="cust-npwp-photo">
                <div className="text-xs text-slate-500 mb-1">Foto NPWP</div>
                <a href={`${API_BASE}/files/${detail.npwp_photo}`} target="_blank" rel="noreferrer">
                  <img src={`${API_BASE}/files/${detail.npwp_photo}`} alt="NPWP" className="w-40 rounded-lg border border-slate-200" />
                </a>
              </div>
            )}
            <Section icon={FileText} title="Sales Order / Project" items={detail.projects} render={(p) => (
              <div key={p.id} className="flex justify-between text-sm border-b border-slate-100 pb-1.5"><span className="font-mono text-xs">{p.code}</span><span className="flex-1 px-3">{p.name}</span><Badge>{p.status}</Badge></div>
            )} />
            <Section icon={ClipboardList} title="Work Order" items={detail.work_orders} render={(w) => (
              <div key={w.id} className="flex justify-between text-sm border-b border-slate-100 pb-1.5"><span className="font-mono text-xs">{w.code}</span><Badge>{w.status}</Badge></div>
            )} />
            <Section icon={Receipt} title="Invoice History" items={detail.invoices} render={(q) => (
              <div key={q.id} className="flex justify-between text-sm border-b border-slate-100 pb-1.5"><span className="font-mono text-xs">{q.code}</span><span className="flex-1 px-3">{formatIDR(q.quote?.total || 0)}</span><Badge>{q.status}</Badge></div>
            )} />
            <Section icon={CalendarCheck} title="Riwayat Kunjungan" items={detail.bookings} render={(b) => (
              <div key={b.id} className="border border-slate-100 rounded-lg p-2.5 space-y-2" data-testid={`cust-booking-${b.code}`}>
                <div className="flex justify-between items-center text-sm">
                  <span className="font-mono text-xs">{b.code}</span>
                  <span className="flex-1 px-3 text-slate-600">{b.date} {b.time}{b.visit_type ? ` · ${b.visit_type}` : ""}</span>
                  <Badge>{b.status}</Badge>
                </div>
                {(b.keterangan || b.conversation) && (
                  <div className="text-xs text-slate-500 bg-slate-50 rounded-md px-2 py-1.5" data-testid={`cust-booking-note-${b.code}`}>
                    {b.keterangan && <div><span className="text-slate-400">Keterangan: </span>{b.keterangan}</div>}
                    {b.conversation && <div><span className="text-slate-400">Hasil pembicaraan: </span>{b.conversation}</div>}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 items-center">
                  {(b.photos || []).map((p) => (
                    <div key={p.id} className="relative group" data-testid={`cust-booking-photo-${p.id}`}>
                      <a href={`${API_BASE}/files/${p.path}`} target="_blank" rel="noreferrer" title={p.note || p.filename}>
                        <img src={`${API_BASE}/files/${p.path}`} alt={p.filename} className="w-16 h-16 object-cover rounded-md border border-slate-200" />
                      </a>
                      {p.note && <div className="text-[9px] text-slate-500 w-16 mt-0.5 leading-tight line-clamp-2" title={p.note}>{p.note}</div>}
                      <button onClick={() => deleteVisitPhoto(b.id, p.id)} data-testid={`cust-booking-photo-del-${p.id}`}
                        className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-2.5 h-2.5" /></button>
                    </div>
                  ))}
                  <label data-testid={`cust-booking-upload-${b.code}`} className="w-16 h-16 border-2 border-dashed border-slate-300 rounded-md flex items-center justify-center text-slate-400 hover:border-sky-400 hover:text-sky-500 cursor-pointer transition-colors">
                    {uploadingBid === b.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                    <input type="file" accept="image/*" multiple className="hidden" disabled={uploadingBid === b.id} onChange={(e) => uploadVisitPhoto(b.id, e.target.files)} />
                  </label>
                </div>
              </div>
            )} />
            <Section icon={Package} title="Titip Barang (Unit di FOMU)" items={detail.consignments} render={(t) => (
              <div key={t.id} className="flex justify-between items-center text-sm border-b border-slate-100 pb-1.5" data-testid={`cust-consign-${t.code}`}>
                <span className="font-mono text-xs">{t.code}</span>
                <span className="flex-1 px-3 text-slate-600">{(t.items || []).length} barang</span>
                <Badge>{t.status === "DI_FOMU" ? "DI FOMU" : "DIKEMBALIKAN"}</Badge>
              </div>
            )} />
          </div>
        )}
      </Modal>
    </div>
  );
}
